import { InsurancePayoutChartJS } from "@/components/charts/InsurancePayoutChart";
import Footer from "@/components/shared/Footer";
import NavbarClient from "@/components/shared/NavbarClient";
import { Button } from "@/components/ui/button";
import Container from "@/components/ui/container";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import cacheKeys from "@/config/cache-keys";
import { currencyOptions } from "@/config/currency-options";
import useCurrency from "@/hooks/useCurrency";
import useFormulas from "@/hooks/useFormulas";
import AppService from "@/services/AppService";
import { useQuery } from "@tanstack/react-query";
import { NumberInput } from "@tremor/react";
import { motion } from "framer-motion";
import { ChevronDown, CircleHelpIcon, CoinsIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import InsurancePayoutTable from "./InsurancePayoutTable";

export default function HowdenCalculator() {
	const formulas = useFormulas();

	const { id } = useParams();

	const appService = new AppService();

	const { t } = useTranslation();
	/* Currency Select */
	const { currentCurrency } = useCurrency();

	// Ref for scrolling to the calculator section.
	const calculatorRef = useRef<HTMLDivElement>(null);
	const scrollToCalculator = () => {
		calculatorRef.current?.scrollIntoView({ behavior: "smooth" });
	};
	// Ref for scrolling to introduction.
	const introductionRef = useRef<HTMLDivElement>(null);

	const scrollToIntroduction = () => {
		introductionRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	// Alternativer til dropdown for marital status
	const maritalStatusOptions = [
		{
			label: t("calculator.input.option.marital.married"),
			value: "married",
		},
		{
			label: t("calculator.input.option.marital.partner"),
			value: "partner",
		},
		{
			label: t("calculator.input.option.marital.cohabitant"),
			value: "cohabitant",
		},
		{
			label: t("calculator.input.option.marital.single"),
			value: "single",
		},
	] as const;

	const query = useQuery({
		queryKey: cacheKeys.CLIENT_DATA,
		queryFn: async () => appService.getClientData(id as string),
		enabled: !!id,
	});

	useEffect(() => {
		if (query.data) {
			formulas.setInsurances(query.data?.Result.insurances);
			formulas.setAnnualSalaryInput("600000", query.data.Result.gvalue || 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query.data]);

	if (query.isLoading) return <div>Loading...</div>;

	if (query.isError) return <div>Error: {query.error.message}</div>;

	if (!query.data) return <div>No data</div>;

	return (
		<div className="bg-[radial-gradient(circle_at_bottom,_var(--tw-gradient-stops))] from-moss-green-80 via-moss-green-80 to-moss-green text-soft-white min-h-screen">
			<NavbarClient />
			{/* Hero Section */}
			<Container className="relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
				<header
					className="max-w-7xl
				text-soft-white
				mx-auto
				flex 
				flex-col 
				md:flex-row 
				md:items-center 
				md:justify-between
				text-center 
				md:text-left
				w-full 
				py-8 
				px-4 
				gap-6 
				animate-fadeIn
				pt-12
				"
				>
					{/* Left Column: Headline, Subheading */}
					<div className="w-full md:w-1/2 mb-6 md:mb-0 md:pr-6 pt-12">
						<motion.h1
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.6 }}
							className="text-3xl sm:text-5xl md:text-6xl space-x-3 font-bold mb-4 border-b-2 border-teal pb-4 "
						>
							{t("home.hero.title")}
						</motion.h1>
						<p className="text-md sm:text-lg md:text-md mb-6  text-gray-300 space-x-3 whitespace-pre-line">
							{t("home.hero.description")}
						</p>
						<div className="flex justify-center md:justify-start gap-4">
							<Button
								variant="secondary"
								onClick={scrollToIntroduction}
								className="font-header text-sm sm:text-lg py-6 bg-pistachio hover:bg-pistachio-80"
							>
								{t("home.hero.button")}
								<svg
									className="w-5 h-5 sm:w-6 sm:h-6 animate-bounce"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M19 9l-7 7-7-7"
									/>
								</svg>
							</Button>
						</div>
					</div>
					{/* Right Column: Image */}
					<div className="w-full md:w-1/2 flex pt-12 justify-center">
						<img
							src="/assets/icons/undraw/calculator.svg"
							alt=""
							className="max-w-full h-auto"
						/>
					</div>
				</header>
			</Container>

			{/* introdction */}
			<div ref={introductionRef} className="scroll-mt-12 pt-5">
				<Container className="relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
					<header
						className="
					max-w-7xl
					mx-auto
					rounded-lg
					bg-moss-green-95
					shadow-lg
					flex 
					flex-col 
					md:flex-row 
					md:items-center 
					md:justify-between
					text-center 
					md:text-left
					w-full 
					py-8 
					px-4 
					gap-6 
					animate-fadeIn
					"
					>
						{/* Left side*/}
						<motion.div
							className="md:w-1/2 flex justify-center md:mt-0"
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.5 }}
						>
							<img
								src="/assets/icons/undraw/personal-file.svg"
								alt="Illustrasjon av forsikringskalkulasjon"
								className="w-full max-w-lg h-auto object-contain"
							/>
						</motion.div>
						{/* right side*/}
						<motion.div
							className="md:w-1/2"
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.5 }}
						>
							<h1 className="text-2xl sm:text-3xl font-extrabold text-soft-white border-b-2 border-teal pb-2 inline-block">
								{t("calculator.hero.title")}
							</h1>

							<p className="mt-4 text-md max-w-lg leading-relaxed">
								{t("calculator.hero.sub-1")}

								{t("calculator.hero.content-1")}
							</p>

							<ul className="mt-6 text-md max-w-lg space-y-3 list-none">
								<li>
									<p className="text-teal">{t("calculator.hero.content-2")}</p>
									<p>{t("calculator.hero.content-3")}</p>
								</li>
								<li>
									<p className="text-teal">{t("calculator.hero.content-4")}</p>
									<p>{t("calculator.hero.content-5")}</p>
								</li>
								<li>
									<p className="text-teal">{t("calculator.hero.content-6")}</p>
									<p>{t("calculator.hero.content-7")}</p>
								</li>
								<li>
									<p className="text-teal">{t("calculator.hero.content-8")}</p>
									<p>{t("calculator.hero.content-9")}</p>
								</li>
							</ul>
							<div className="mt-6 flex justify-center md:justify-start">
								<Button
									variant="secondary"
									onClick={scrollToCalculator}
									className="font-header text-lg flex items-center space-x-2 bg-pistachio hover:bg-pistachio-80"
								>
									{t("calculator.hero.button")}
									<svg
										className="w-5 h-5 animate-bounce"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M19 9l-7 7-7-7"
										/>
									</svg>
								</Button>
							</div>
						</motion.div>
					</header>
				</Container>
			</div>

			{/* Main Content: Calculator Section */}
			<div ref={calculatorRef} className="scroll-mt-12 pt-5">
				{/* Calculator Section */}
				<Container className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-start gap-12">
					{/* Left Column: Input Forms */}
					<div className="w-full lg:w-1/2 space-y-10">
						{/* Employee Details */}
						<section className="bg-moss-green-95 rounded-xl p-6 shadow-lg">
							<h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 border-b border-moss-green-60 pb-2">
								{t("calculator.chart.employee.header")}
							</h2>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
								{/* Age Input */}
								<div>
									<Label className="flex justify-between items-center mb-1 text-sm font-medium">
										{t("calculator.chart.employee.age")}
										<Dialog>
											<DialogTrigger asChild>
												<button
													title="Why is your age important?"
													aria-label="Why is your age important?"
												>
													<CircleHelpIcon className="h-4 w-4" />
												</button>
											</DialogTrigger>
											<DialogContent className="rounded-lg max-w-md p-6">
												<DialogHeader>
													<DialogTitle className="text-lg font-bold text-left">
														{t("calculator.chart.employee.age-dialog.title")}
													</DialogTitle>
													<DialogDescription className="text-sm text-left leading-relaxed">
														{t(
															"calculator.chart.employee.age-dialog.content-1"
														)}
														<br />
														{t(
															"calculator.chart.employee.age-dialog.content-2"
														)}
													</DialogDescription>
												</DialogHeader>
											</DialogContent>
										</Dialog>
									</Label>
									<Input
										type="number"
										value={formulas.ageInput}
										onChange={(e) => formulas.setAgeInput(e.target.value)}
										placeholder={t("calculator.input.placeholder.age")}
										className="w-full bg-moss-green-80 border-moss-green-70 text-soft-white rounded-md p-2 focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
									/>
								</div>
								{/* Medical Invalidity */}
								<div>
									<Label className="flex justify-between items-center mb-1 text-sm font-medium">
										{t("calculator.chart.employee.medical-invalidity")}
										<Dialog>
											<DialogTrigger asChild>
												<button
													title="What is medical invalidity?"
													aria-label="Explanation of medical invalidity"
												>
													<CircleHelpIcon className="h-4 w-4" />
												</button>
											</DialogTrigger>
											<DialogContent className="max-w-lg w-full p-4 sm:p-6 rounded-lg shadow-lg overflow-y-auto">
												<DialogHeader>
													<DialogTitle className="text-lg font-bold text-left">
														{t(
															"calculator.chart.employee.medical-invalidity-dialog.title"
														)}
													</DialogTitle>
													<DialogDescription className="text-sm text-left leading-relaxed">
														{t(
															"calculator.chart.employee.medical-invalidity-dialog.content-1"
														)}
														<br />
														{t(
															"calculator.chart.employee.medical-invalidity-dialog.content-2"
														)}
														<img
															src="/assets/clientportal/invaliditet.png"
															alt="Medical invalidity chart"
															className="mt-4 w-full rounded-md"
														/>
													</DialogDescription>
												</DialogHeader>
											</DialogContent>
										</Dialog>
									</Label>
									<Input
										type="number"
										value={formulas.invalidityInput}
										onChange={(e) =>
											formulas.setInvalidityInput(e.target.value)
										}
										placeholder={t(
											"calculator.input.placeholder.medical-invalidity"
										)}
										className="w-full bg-moss-green-80 border-moss-green-70 text-soft-white rounded-md p-2 focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
									/>
								</div>
								{/* Income */}
								<div>
									<Label className="flex justify-between items-center mb-1 text-sm font-medium">
										{t("calculator.chart.employee.income")}
										<Dialog>
											<DialogTrigger asChild>
												<button
													title="Why is income relevant?"
													aria-label="Why is income relevant?"
												>
													<CircleHelpIcon className="h-4 w-4" />
												</button>
											</DialogTrigger>
											<DialogContent className="max-w-lg w-full p-4 sm:p-6 rounded-lg shadow-lg ">
												<DialogHeader>
													<DialogTitle className="text-lg font-bold text-left">
														{t("calculator.chart.employee.income-dialog.title")}
													</DialogTitle>
													<DialogDescription className="text-sm text-left leading-relaxed space-y-4">
														{t(
															"calculator.chart.employee.income-dialog.content-1"
														)}
														<br />
														{t(
															"calculator.chart.employee.income-dialog.content-2"
														)}
													</DialogDescription>
												</DialogHeader>
											</DialogContent>
										</Dialog>
									</Label>
									<Input
										type="number"
										value={formulas.annualSalaryInput}
										onChange={(e) =>
											formulas.setAnnualSalaryInput(
												e.target.value,
												query.data.Result.gvalue
											)
										}
										placeholder={t("calculator.input.placeholder.income")}
										className="w-full bg-moss-green-80 border-moss-green-70 text-soft-white rounded-md p-2 focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
									/>
								</div>
								{/* Occupational Disability */}
								<div>
									<Label className="flex justify-between items-center mb-1 text-sm font-medium">
										{t("calculator.chart.employee.occupational-disability")}
										<Dialog>
											<DialogTrigger asChild>
												<button
													title="What is occupational disability?"
													aria-label="Explanation of occupational disability"
												>
													<CircleHelpIcon className="h-4 w-4" />
												</button>
											</DialogTrigger>
											<DialogContent className="max-w-lg w-full p-4 sm:p-6 rounded-lg shadow-lg overflow-y-auto max-h-screen">
												<DialogHeader>
													<DialogTitle className="text-lg font-bold text-left">
														{t(
															"calculator.chart.employee.occupational-disability-dialog.title"
														)}
													</DialogTitle>
													<DialogDescription className="text-sm text-left leading-relaxed space-y-4">
														{t(
															"calculator.chart.employee.occupational-disability-dialog.content-1"
														)}
														<br />
														{t(
															"calculator.chart.employee.occupational-disability-dialog.content-2"
														)}
														<ul className="list-disc pl-5 mt-2 space-y-1">
															<li>
																{t(
																	"calculator.chart.employee.occupational-disability-dialog.content-3"
																)}
																<br />
																{t(
																	"calculator.chart.employee.occupational-disability-dialog.content-4"
																)}
															</li>
															<li>
																{t(
																	"calculator.chart.employee.occupational-disability-dialog.content-5"
																)}
															</li>
															<li>
																{t(
																	"calculator.chart.employee.occupational-disability-dialog.content-6"
																)}
															</li>
														</ul>
														{t(
															"calculator.chart.employee.occupational-disability-dialog.content-7"
														)}
														<br />
														<a
															href="https://lovdata.no/dokument/SF/forskrift/1990-12-21-1027/KAPITTEL_3#%C2%A73-1"
															target="_blank"
															rel="noopener noreferrer"
															className="text-mustard underline"
														>
															LovData
														</a>
													</DialogDescription>
												</DialogHeader>
											</DialogContent>
										</Dialog>
									</Label>
									<Input
										type="number"
										value={formulas.disabilityInput}
										onChange={(e) =>
											formulas.setDisabilityInput(e.target.value)
										}
										placeholder={t(
											"calculator.input.placeholder.occupational-disability"
										)}
										className="w-full bg-moss-green-80 border-moss-green-70 text-soft-white rounded-md p-2 focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
									/>
								</div>
							</div>
						</section>

						{/* Family Status Section */}
						<section className="bg-moss-green-95 rounded-xl p-6 shadow-lg">
							<h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 border-b border-moss-green-60 pb-2">
								{t("calculator.chart.family.header")}
							</h2>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								{/* Marital Status Input */}
								<div>
									<Label className="flex justify-between items-center mb-1 text-sm font-medium">
										{t("calculator.chart.family.marital")}
										<Dialog>
											<DialogTrigger asChild>
												<button
													className="cursor-pointer items-center"
													title="Why does marital status matter?"
													aria-label="Why does marital status matter?"
												>
													<CircleHelpIcon
														size={14}
														className="inline text-soft-white justify-center"
													/>
												</button>
											</DialogTrigger>
											<DialogContent
												className="max-w-lg w-full p-4 sm:p-6 rounded-lg shadow-lg overflow-y-auto max-h-screen"
												aria-labelledby="marital-status-dialog-title"
												aria-describedby="marital-status-dialog-description"
											>
												<DialogHeader>
													<DialogTitle className="text-lg font-bold text-left">
														{t("calculator.chart.family.marital-dialog.title")}
													</DialogTitle>
													<DialogDescription
														id="marital-status-dialog-description"
														className="text-sm text-left space-y-3 leading-relaxed"
													>
														{t(
															"calculator.chart.family.marital-dialog.content-1"
														)}
														<br />
														{t(
															"calculator.chart.family.marital-dialog.content-2"
														)}
														<br />
														<br />
														{t(
															"calculator.chart.family.marital-dialog.content-3"
														)}
													</DialogDescription>
												</DialogHeader>
											</DialogContent>
										</Dialog>
									</Label>
									<div className="flex items-center gap-2">
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<div className="relative w-full">
													<Input
														value={
															formulas.maritalStatusInput?.label ||
															t("calculator.input.placeholder.select-marital")
														}
														readOnly
														className="w-full text-soft-white text-left rounded-md border bg-moss-green-80 border-moss-green-70 p-2 pr-10 focus:outline-none focus:ring-2 focus:ring-mustard"
														aria-label={t(
															"calculator.input.placeholder.select-marital"
														)}
													/>
													<span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
														<ChevronDown className="h-4 w-4 text-gray-400" />
													</span>
												</div>
											</DropdownMenuTrigger>
											<DropdownMenuContent
												align="start"
												sideOffset={0}
												className="w-full"
											>
												{maritalStatusOptions.map((option) => (
													<DropdownMenuItem
														key={option.label}
														onClick={() =>
															formulas.setMaritalStatusInput(option)
														}
													>
														{option.label}
													</DropdownMenuItem>
												))}
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								</div>
								{/* Spouse Age Input */}
								{/* <div>
									<Label className="flex justify-between items-center mb-1 text-sm font-medium">
										{t("calculator.chart.family.age-partner")}
										<Dialog>
											<DialogTrigger asChild>
												<button
													className="cursor-pointer items-center"
													title="Why is spouse age relevant?"
													aria-label="Why is spouse age relevant?"
												>
													<CircleHelpIcon
														size={14}
														className="inline text-soft-white justify-center"
													/>
												</button>
											</DialogTrigger>
											<DialogContent className="max-w-lg w-full p-4 sm:p-6 rounded-lg shadow-lg overflow-y-auto max-h-screen">
												<DialogHeader>
													<DialogTitle className="text-lg font-bold text-left">
														{t(
															"calculator.chart.family.age-partner-dialog.title"
														)}
													</DialogTitle>
													<DialogDescription className="text-sm text-left space-y-3 leading-relaxed">
														{t(
															"calculator.chart.family.age-partner-dialog.content-1"
														)}
														<br />
														<br />
														{t(
															"calculator.chart.family.age-partner-dialog.content-2"
														)}
														<br />
														<br />
														<strong>
															{t(
																"calculator.chart.family.age-partner-dialog.content-3"
															)}
														</strong>
														{t(
															"calculator.chart.family.age-partner-dialog.content-4"
														)}
													</DialogDescription>
												</DialogHeader>
											</DialogContent>
										</Dialog>
									</Label>
									<Input
										value={formulas.spouseAgeInput}
										onChange={(event) => {
											const value = event.target.value;
											formulas.setSpouseAgeInput(value);
										}}
										placeholder={t("calculator.input.placeholder.partner-age")}
										className="w-full placeholder-white bg-moss-green-80 border-moss-green-70 text-soft-white rounded-md p-2 focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
									/>
								</div> */}
								{/* Sole Provider Input */}
								<div>
									<Label className="flex justify-between items-center mb-1 text-sm font-medium">
										{t("calculator.chart.family.sole-provider")}
										<Dialog>
											<DialogTrigger asChild>
												<button
													className="cursor-pointer items-center"
													title="Why does being a sole provider matter?"
													aria-label="Why does being a sole provider matter?"
												>
													<CircleHelpIcon
														size={14}
														className="inline text-soft-white justify-center"
													/>
												</button>
											</DialogTrigger>
											<DialogContent
												className="max-w-lg w-full p-4 sm:p-6 rounded-lg shadow-lg overflow-y-auto max-h-screen"
												aria-labelledby="sole-provider-dialog-title"
												aria-describedby="sole-provider-dialog-description"
											>
												<DialogHeader>
													<DialogTitle
														id="sole-provider-dialog-title"
														className="text-lg font-bold text-left"
													>
														{t(
															"calculator.chart.family.sole-provider-dialog.title"
														)}
													</DialogTitle>
													<DialogDescription
														id="sole-provider-dialog-description"
														className="text-sm text-left space-y-3leading-relaxed"
													>
														{t(
															"calculator.chart.family.sole-provider-dialog.content-1"
														)}
														<br />
														<br />
														{t(
															"calculator.chart.family.sole-provider-dialog.content-2"
														)}
														<strong>
															{" "}
															{t(
																"calculator.chart.family.sole-provider-dialog.content-3"
															)}
														</strong>
													</DialogDescription>
												</DialogHeader>
											</DialogContent>
										</Dialog>
									</Label>
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<div className="relative w-full">
												<Input
													value={
														formulas.soleProviderInput?.label ||
														t(
															"calculator.input.placeholder.select-sole-provider"
														)
													}
													readOnly
													className="w-full bg-moss-green-80 border-moss-green-70 text-soft-white rounded-md p-2 focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
													aria-label={t(
														"calculator.input.placeholder.select-sole-provider"
													)}
												/>
												<span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
													<ChevronDown className="h-4 w-4 text-gray-400" />
												</span>
											</div>
										</DropdownMenuTrigger>
										<DropdownMenuContent
											align="start"
											sideOffset={0}
											className="w-full"
										>
											{[
												{
													label: t("calculator.input.option.sole-provider.yes"),
													value: true,
												},
												{
													label: t("calculator.input.option.sole-provider.no"),
													value: false,
												},
											].map((option) => (
												<DropdownMenuItem
													key={option.label}
													onClick={() => formulas.setSoleProviderInput(option)}
												>
													{option.label}
												</DropdownMenuItem>
											))}
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
								{/* Children Under 25 Input */}
								<div>
									<Label className="flex justify-between items-center mb-1 text-sm font-medium">
										{t("calculator.chart.family.children")}
										<Dialog>
											<DialogTrigger asChild>
												<button
													className="cursor-pointer items-center"
													title="How children's age affects compensation"
													aria-label="How children's age affects compensation"
												>
													<CircleHelpIcon
														size={14}
														className="inline text-soft-white justify-center"
													/>
												</button>
											</DialogTrigger>
											<DialogContent
												className="max-w-lg w-full p-4 sm:p-6 rounded-lg shadow-lg overflow-y-auto max-h-screen"
												aria-labelledby="children-age-dialog-title"
												aria-describedby="children-age-dialog-description"
											>
												<DialogHeader>
													<DialogTitle
														id="children-age-dialog-title"
														className="text-lg font-bold text-left"
													>
														{t("calculator.chart.family.children-dialog.title")}
													</DialogTitle>
													<DialogDescription
														id="children-age-dialog-description"
														className="text-sm text-left space-y-3 leading-relaxed"
													>
														{t(
															"calculator.chart.family.children-dialog.content-1"
														)}
														<br />
														<br />
														{t(
															"calculator.chart.family.children-dialog.content-2"
														)}
														<br />
														<br />
														{t(
															"calculator.chart.family.children-dialog.content-3"
														)}
														<br />
														<br />
														{t(
															"calculator.chart.family.children-dialog.content-4"
														)}
														<strong>
															{t(
																"calculator.chart.family.children-dialog.content-5"
															)}
														</strong>
														{t(
															"calculator.chart.family.children-dialog.content-6"
														)}
														<br />
														<br />
														{t(
															"calculator.chart.family.children-dialog.content-7"
														)}
													</DialogDescription>
												</DialogHeader>
											</DialogContent>
										</Dialog>
									</Label>
									<NumberInput
										value={formulas.numOfChildrenInput}
										// onValueChange={(newValue: number) => {
										// 	const limited = Math.min(newValue, 8);
										// 	setChildrenUnder25(limited.toString());
										// 	setChildrenAges((prev) => {
										// 		if (prev.length < limited) {
										// 			return [
										// 				...prev,
										// 				...Array(limited - prev.length).fill(""),
										// 			];
										// 		} else if (prev.length > limited) {
										// 			return prev.slice(0, limited);
										// 		}
										// 		return prev;
										// 	});
										// }}

										onValueChange={(newValue: number) => {
											formulas.setNumOfChildrenInput(newValue.toString());
										}}
										placeholder={t("calculator.input.placeholder.children")}
										min={0}
										className="w-full bg-moss-green-80 border-moss-green-70 rounded-md focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
									/>
								</div>
								{/* Legge til inputfelt for barnas alder */}
								{formulas.numOfChildren > 0 && (
									<div className="col-span-2">
										<div className="grid grid-cols-2 gap-6 mt-4">
											{formulas.childAgesInput.map((childAge, idx) => (
												<div key={idx}>
													<Label className="block mb-1 text-sm font-medium">
														{t("calculator.chart.family.child-age")} {idx + 1}
													</Label>
													<Input
														type="number"
														value={childAge}
														onChange={(e) => {
															const prev = formulas.childAgesInput;
															const updated = [...prev];
															updated[idx] = e.target.value;
															formulas.setChildAgesInput(updated);
														}}
														placeholder={t(
															"calculator.input.placeholder.child-age"
														)}
														className="w-full bg-moss-green-80 border-moss-green-70 rounded-md focus:ring-2 focus:ring-teal focus:border-transparent transition-all"
													/>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</section>
						{/* Disclaimer*/}
						<p className="text-xs text-gray-300 max-w-xl">
							<strong>{t("calculator.chart.disclaimer.title")} </strong>
							{t("calculator.chart.disclaimer.content-1")}
						</p>
					</div>

					{/* RIGHT COLUMN: Chart */}
					<section className="bg-moss-green-95 rounded-xl p-4 sm:p-6 shadow-lg w-full space-y-6 flex flex-col">
						<h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 border-b border-moss-green-60 pb-2">
							{t("calculator.chart.benefits.header")}
						</h2>
						<div className="flex flex-row justify-between space-y-0">
							<CurrencySelect currentCurrency={currentCurrency} />
							<Dialog>
								<DialogTrigger asChild>
									<Button
										variant="secondary"
										className="flex space-x-2 text-xs sm:text-sm px-3 py-2 w-auto bg-pistachio hover:bg-pistachio-80"
									>
										<CircleHelpIcon size={12} />
										<span>{t("calculator.chart.benefits.dialog.title")}</span>
									</Button>
								</DialogTrigger>
								<DialogContent className="w-full p-4 sm:p-6 bg-white rounded-lg shadow-lg max-h-[80vh] overflow-y-auto">
									<DialogHeader>
										<DialogTitle className="text-base sm:text-lg font-bold text-left">
											{t("calculator.chart.benefits.dialog.dialog-title")}
										</DialogTitle>
										<DialogDescription className="text-xs sm:text-sm text-left space-y-3">
											<p>{t("calculator.chart.benefits.dialog.content-1")}</p>
											<p>
												{t("calculator.chart.benefits.dialog.content-2")}
												<strong>
													{" "}
													{t("calculator.chart.benefits.dialog.content-3")}{" "}
												</strong>
												{t("calculator.chart.benefits.dialog.content-4")}
												<strong>
													{" "}
													{t("calculator.chart.benefits.dialog.content-5")}{" "}
												</strong>
												{t("calculator.chart.benefits.dialog.content-6")}
												<strong>
													{" "}
													{t("calculator.chart.benefits.dialog.content-7")}{" "}
												</strong>
												{t("calculator.chart.benefits.dialog.content-8")}
											</p>
											<p>
												{t("calculator.chart.benefits.dialog.content-9")}
												<strong>
													{" "}
													{t(
														"calculator.chart.benefits.dialog.content-10"
													)}{" "}
												</strong>
												{t("calculator.chart.benefits.dialog.content-11")}
												<strong>
													{" "}
													{t(
														"calculator.chart.benefits.dialog.content-12"
													)}{" "}
												</strong>
												{t("calculator.chart.benefits.dialog.content-13")}
												{t("calculator.chart.benefits.dialog.content-14")}
											</p>
											<p>{t("calculator.chart.benefits.dialog.content-15")}</p>
											<p>{t("calculator.chart.benefits.dialog.content-16")}</p>
											<p>{t("calculator.chart.benefits.dialog.content-17")}</p>
											<a
												href="https://www.nav.no/grunnbelopet"
												target="_blank"
												rel="noopener noreferrer"
												className="text-mustard underline"
											>
												{t("calculator.chart.benefits.dialog.content-18")}
											</a>
										</DialogDescription>
									</DialogHeader>
								</DialogContent>
							</Dialog>
						</div>
						{/* Dynamic Chart Component */}
						<div className="relative min-w-[320px] h-[350px] sm:h-[350px] md:h-[400px]">
							<InsurancePayoutChartJS
								currency={currentCurrency} // Pass the selected currency
							/>
						</div>

						<div>
							<InsurancePayoutTable currency={currentCurrency} />
						</div>
					</section>
				</Container>
			</div>
			<Footer />
		</div>
	);
}

function CurrencySelect({ currentCurrency }: { currentCurrency: string }) {
	const { t } = useTranslation();
	const { setCurrency } = useCurrency();

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button className="flex space-x-1 border bg-moss-green-80 border-moss-green-70">
					<CoinsIcon />
					<span className="text-sm">{currentCurrency}</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="min-w-60">
				<DropdownMenuLabel>{t("currency.select")}</DropdownMenuLabel>
				{currencyOptions.map((option) => (
					<DropdownMenuCheckboxItem
						key={option.value}
						checked={currentCurrency === option.value}
						onCheckedChange={() => setCurrency(option.value)}
					>
						<div className="flex justify-between items-center w-full">
							<p>{t(option.label)}</p>
							<p>{option.value}</p>
						</div>
					</DropdownMenuCheckboxItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
